import { Box, SelectChangeEvent, Typography, useTheme } from '@mui/material';

import { useAuth } from '@verantos-dev/core-auth';
import { OrganizationsDetails } from '@verantos-dev/data-access-organizations';
import { ApiWorkflowDialog, Dropdown, TextField } from '@verantos-dev/shared-components';

import { useOrganizationsListCreateEditDialog } from '../hooks/useOrganizationsListCreateEditDialog';
import { useOrganizationsListCreateEditDialogStrings } from '../hooks/useOrganizationsListCreateEditDialogStrings';

export type CreateEditOrganizationType = 'createOrganization' | 'editOrganization';
type CreateEditOrganizationPropsType = {
  dialogType?: CreateEditOrganizationType;
  organizationData?: OrganizationsDetails;
};

export const OrganizationsListCreateEditDialog = (props: CreateEditOrganizationPropsType) => {
  const { dialogType = 'createOrganization', organizationData } = props;

  const { user } = useAuth();
  const isInternalUser = user?.isInternalUser;

  const theme = useTheme();

  const {
    name,
    type,
    subType,

    nameErrorCode,

    onChangeName,
    onChangeType,
    onChangeSubType,

    isApiLoading,
    apiError,
    appErrorMessage,

    validToCreateEdit,
    typeOptions,
    subTypeOptions,

    organizationKey,
    onChangeOrganizationKey,

    onCancel,
    onSubmit,
  } = useOrganizationsListCreateEditDialog(dialogType, organizationData);

  const { dialogStrings, apiWorkflowDialogMessages } = useOrganizationsListCreateEditDialogStrings(
    dialogType,
    appErrorMessage
  );

  return (
    <ApiWorkflowDialog
      dialogMessages={apiWorkflowDialogMessages}
      id={'organizationListCreateDialog'}
      title={dialogStrings.title}
      size="medium"
      isValidForSubmit={validToCreateEdit}
      isSubmitting={isApiLoading}
      submittingError={apiError}
      onClose={onCancel}
      onSubmit={onSubmit}
      hideDisplayButtonsOnSuccess
    >
      <Box style={{ display: 'flex', flexDirection: 'column', gap: `${theme.spacing(6)}` }}>
        <Typography variant="body2">{dialogStrings.description}</Typography>
        <Box style={{ display: 'flex', flexDirection: 'row', gap: `${theme.spacing(6)}` }}>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(6),
              flex: 1,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: '600',
                lineHeight: '24px',
                color: theme.palette.text.primary,
                marginBottom: theme.spacing(3),
              }}
            >
              {dialogStrings.organizationDetailsSection}
            </Typography>
            <TextField
              id="createEdit-organization-name-input"
              type="text"
              width={'auto'}
              displayLabel={dialogStrings.organizationNameLabel}
              value={name || ''}
              placeholder={dialogStrings.organizationNamePlaceholder}
              onChange={onChangeName}
              maxCharacters={50}
              maxCharactersReachedMsg={dialogStrings.organizationNameMaxLength}
              beepMaxCharacters={true}
              excludeLeadTailSpace={true}
              helperText={
                nameErrorCode ? dialogStrings[nameErrorCode as keyof typeof dialogStrings] : ' '
              }
              disabled={isApiLoading}
              displayNumCharacter
              error={!!nameErrorCode}
            />
            <Dropdown
              id={'createEdit-organizations-type-dropdown'}
              label={dialogStrings.organizationTypeLabel}
              placeholder={dialogStrings.organizationTypePlaceholder}
              width={'auto'}
              options={typeOptions}
              onChange={(event, child) => onChangeType(event as SelectChangeEvent<string>)}
              value={type}
              disabled={isApiLoading || dialogType === 'editOrganization'}
              sx={{ marginBottom: '21px' }}
            ></Dropdown>
            <Dropdown
              id={'createEdit-organizations-subType-dropdown'}
              label={dialogStrings.organizationSubtypeLabel}
              placeholder={dialogStrings.organizationSubtypePlaceholder}
              width={'auto'}
              options={subTypeOptions}
              onChange={(event, child) => onChangeSubType(event as SelectChangeEvent<string>)}
              value={subType}
              disabled={isApiLoading || type === ''}
              sx={{ marginBottom: '21px' }}
            ></Dropdown>
            {isInternalUser && (
              <TextField
                id="createEdit-organization-key-input"
                type="text"
                width={'auto'}
                displayLabel={dialogStrings.organizationKeyLabel}
                value={organizationKey || ''}
                placeholder={dialogStrings.organizationKeyPlaceholder}
                onChange={onChangeOrganizationKey}
                maxCharacters={50}
                maxCharactersReachedMsg={dialogStrings.organizationNameMaxLength}
                beepMaxCharacters={true}
                excludeLeadTailSpace={true}
                disabled={isApiLoading}
                displayNumCharacter
              />
            )}
          </Box>
        </Box>
      </Box>
    </ApiWorkflowDialog>
  );
};

export default OrganizationsListCreateEditDialog;
